import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Sparkles, Image as ImageIcon, Type, Music, Mic, Palette,
  Check, X, Upload, ChevronRight, ChevronLeft, Loader2,
  Zap, SlidersHorizontal, Wand2, AlertTriangle, Info, Film,
  Play, Pause
} from 'lucide-react';
import { musicAPI, videoAPI } from '../lib/api';

const TOTAL_STEPS = 5;

const COLOR_PRESETS = [
  { name: 'Default', title: '', bg: '', accent: '' },
  { name: 'Midnight Blue', title: '#1e3a5f', bg: '#0a1929', accent: '#4fc3f7' },
  { name: 'Warm Gold', title: '#b8860b', bg: '#1a1a0a', accent: '#ffd700' },
  { name: 'Emerald', title: '#006d5b', bg: '#0a1f1a', accent: '#00e676' },
  { name: 'Rose', title: '#9e1b5a', bg: '#1a0a12', accent: '#ff4081' },
  { name: 'Slate', title: '#475569', bg: '#0f172a', accent: '#94a3b8' },
];

const STEP_LABELS = ['Mode', 'Media', 'Text', 'Style', 'Audio'];

export default function VideoGenerationWizard({ 
  property, 
  template, 
  onGenerate, 
  onCancel,
  generatingVideo 
}) {
  // Template configuration
  const mediaCount = template.media_count || 5;
  const mediaType = template.media_type || 'image';
  const requiresVideos = mediaType === 'video';
  const textFieldsList = template.text_fields || [];
  const hasVoiceover = template.has_voiceover === true;
  const hasBackAudio = template.has_background_audio !== false;

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('ai');
  const [language, setLanguage] = useState('de');
  
  // Media selection
  const [selectedMedia, setSelectedMedia] = useState([]);
  
  // Text fields state
  const [textFields, setTextFields] = useState({
    title: '',
    cta: '',
    details: '',
    details_1: '',
    details_2: '',
    address: '',
    description: '',
    subtext: '',
    promo: '',
    text: '',
    amount: '',
    price: '',
    voiceover: '',
  });
  const [aiAssistLoading, setAiAssistLoading] = useState(null);
  
  // Style & branding
  const [colorPreset, setColorPreset] = useState(0);
  const [customColors, setCustomColors] = useState({ title: '', bg: '', accent: '' });
  const [showBranding, setShowBranding] = useState(true);
  
  // Music
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [presetMusic, setPresetMusic] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null); // ID of currently playing preset
  // useRef instead of useState({current: null}). Functionally identical
  // since we only ever mutate `.current`, but useRef is the React-idiomatic
  // way and skips the unused state tuple's setter.
  const audioRef = useRef(null);
  
  // Voiceover
  const [enableVoiceover, setEnableVoiceover] = useState(hasVoiceover);
  const [musicVolume, setMusicVolume] = useState(enableVoiceover ? '30%' : '100%');

  // Helper to format Euro prices: €620000.0 -> € 620.000,00
  const formatEuro = (amount) => {
    if (!amount) return '';
    let cleaned = String(amount).replace(/[€$£\s]/g, '');
    
    // Handle decimal values like "620000.0" - take only integer part
    if (cleaned.includes('.')) {
      cleaned = cleaned.split('.')[0];
    }
    
    // Remove commas (US format)
    cleaned = cleaned.replace(/,/g, '');
    
    try {
      const value = parseInt(cleaned);
      if (isNaN(value)) return amount;
      // Format with German locale (dots as thousands separator)
      const formatted = value.toLocaleString('de-DE');
      return `€ ${formatted},00`;
    } catch {
      return amount;
    }
  };

  // Build a single object of sensible default text values for all template
  // fields, localized by `language`. Used on mount, in per-field AI assist,
  // and in "AI fill all". Centralising the strings here keeps German and
  // English in sync.
  const buildTextDefaults = (prop, lang) => {
    if (!prop) return {};
    const highlights = prop.highlights || [];
    const formattedPrice = formatEuro(prop.price);
    const isDE = lang === 'de';
    return {
      text: isDE ? 'Neues Angebot' : 'New Listing',
      title: prop.title?.slice(0, 30) || (isDE ? 'Traumimmobilie' : 'Dream Property'),
      cta: isDE ? 'Jetzt anfragen!' : 'Inquire now!',
      details: highlights.slice(0, 3).join(' · ').slice(0, 60) || '',
      details_1: (highlights[0] || '').slice(0, 10),
      details_2: (highlights[1] || '').slice(0, 10),
      details_3: (highlights[2] || '').slice(0, 10),
      address: prop.location?.slice(0, 80) || '',
      description: `${prop.title || ''}\n${prop.location || ''}`.slice(0, 100),
      subtext: isDE ? 'Jetzt Online' : 'Now Available',
      promo: isDE ? 'Jetzt Anrufen' : 'Call Now',
      amount: formattedPrice,
      price: formattedPrice,
      price_text: isDE ? 'Jetzt Online für' : 'Now Available for',
      voiceover: isDE
        ? 'Entdecken Sie diese einzigartige Immobilie in bester Lage. Kontaktieren Sie uns für mehr Informationen.'
        : 'Discover this unique property in a prime location. Contact us for more information.',
    };
  };

  // Pre-fill text fields with property data on mount
  useEffect(() => {
    if (property) {
      setTextFields(buildTextDefaults(property, language));
    }
    // buildTextDefaults captures property/language via closure; re-run on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, language]);

  // Initialize media selection
  useEffect(() => {
    if (property?.images && !requiresVideos) {
      // For image templates, pre-select first N images
      setSelectedMedia(property.images.slice(0, mediaCount));
    } else {
      setSelectedMedia([]);
    }
    loadMusicLibrary();
  }, [property, mediaCount, requiresVideos]);

  useEffect(() => {
    setMusicVolume(enableVoiceover ? '30%' : '100%');
  }, [enableVoiceover]);

  useEffect(() => {
    if (colorPreset > 0) {
      const preset = COLOR_PRESETS[colorPreset];
      setCustomColors({ title: preset.title, bg: preset.bg, accent: preset.accent });
    } else {
      setCustomColors({ title: '', bg: '', accent: '' });
    }
  }, [colorPreset]);

  const loadMusicLibrary = async () => {
    try {
      const [libraryRes, presetsRes] = await Promise.all([
        musicAPI.getLibrary().catch(() => ({ data: [] })),
        musicAPI.getPresets().catch(() => ({ data: [] })),
      ]);
      setMusicLibrary(Array.isArray(libraryRes.data) ? libraryRes.data : []);
      setPresetMusic(Array.isArray(presetsRes.data) ? presetsRes.data : []);
    } catch (error) {
      // User sees an empty music tab. Let them know why rather than
      // silently looking broken.
      console.error('Failed to load music library:', error);
      if (error?.response?.status !== 401) {
        toast.warning(
          language === 'de'
            ? 'Musikbibliothek konnte nicht geladen werden. Fahren Sie ohne Musik fort.'
            : "Couldn't load music library. Continue without music."
        );
      }
    }
  };

  // Audio preview playback.
  //
  // Selecting a track and previewing it are now ONE action: choosing a track
  // immediately plays it so the user hears their choice. This fixes the
  // painpoint where pressing play did NOT select the track, so users ended up
  // generating videos with no music at all.
  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingPreview(null);
  };

  const startPreview = (presetId, presetUrl) => {
    // Stop any current playback first
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(presetUrl);
    audio.volume = 0.5;
    audio.onended = () => setPlayingPreview(null);
    audio.play().catch(() => toast.error('Could not play audio preview'));
    audioRef.current = audio;
    setPlayingPreview(presetId);
  };

  // Select a track AND preview it in one go (used when clicking a track card).
  const selectTrack = (presetId, presetUrl) => {
    setSelectedMusic(presetUrl);
    startPreview(presetId, presetUrl);
  };

  // Play/pause button: toggles audio for a track WITHOUT ever de-selecting it.
  // Pressing play on a not-yet-selected track also selects it, so the user can
  // never end up "previewing but not selected".
  const togglePreview = (presetId, presetUrl) => {
    if (playingPreview === presetId) {
      stopPreview();
    } else {
      setSelectedMusic(presetUrl);
      startPreview(presetId, presetUrl);
    }
  };

  // Explicit "no music" choice so users can opt out deliberately.
  const clearMusic = () => {
    stopPreview();
    setSelectedMusic(null);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleMediaToggle = (url) => {
    if (selectedMedia.includes(url)) {
      setSelectedMedia(selectedMedia.filter(m => m !== url));
    } else {
      if (selectedMedia.length < mediaCount) {
        setSelectedMedia([...selectedMedia, url]);
      } else {
        toast.error(`This template requires exactly ${mediaCount} ${mediaType}(s)`);
      }
    }
  };

  const handleSelectAll = () => {
    if (property?.images) {
      setSelectedMedia(property.images.slice(0, mediaCount));
    }
  };

  const handleMusicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMusic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      const response = await musicAPI.upload(formData);
      setMusicLibrary([response.data, ...musicLibrary]);
      setSelectedMusic(response.data.file_url);
      toast.success('Music uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload music');
    } finally {
      setUploadingMusic(false);
    }
  };

  const handleAiAssistField = async (fieldName) => {
    setAiAssistLoading(fieldName);
    try {
      const suggestions = buildTextDefaults(property, language);
      setTextFields(prev => ({ ...prev, [fieldName]: suggestions[fieldName] || '' }));
      toast.success(
        language === 'de' ? `Feld „${fieldName}“ ausgefüllt` : `AI filled ${fieldName}`
      );
    } catch (e) {
      console.error('AI assist error:', e);
      toast.error(language === 'de' ? 'KI-Vorschlag fehlgeschlagen' : 'AI assist failed');
    } finally {
      setAiAssistLoading(null);
    }
  };

  const handleAiAssistAll = async () => {
    setAiAssistLoading('all');
    try {
      setTextFields(buildTextDefaults(property, language));
      toast.success(
        language === 'de'
          ? 'Alle Felder mit KI-Vorschlägen ausgefüllt'
          : 'All fields filled with AI suggestions'
      );
    } catch (e) {
      console.error('AI assist error:', e);
      toast.error(language === 'de' ? 'KI-Vorschlag fehlgeschlagen' : 'AI assist failed');
    } finally {
      setAiAssistLoading(null);
    }
  };

  const handleGenerate = () => {
    const data = {
      property_id: property.id,
      template_id: template.id,
      language,
      manual_mode: mode === 'manual',
      enable_voiceover: enableVoiceover && hasVoiceover,
      selected_images: selectedMedia,
    };

    if (mode === 'manual') {
      data.text_fields = textFields;
      data.voiceover_text = textFields.voiceover;
    }

    if (selectedMusic) {
      data.music_url = selectedMusic;
      data.music_volume = musicVolume;
    }

    // Style customizations
    const customizations = {};
    if (customColors.title) customizations.title_color = customColors.title;
    if (customColors.bg) customizations.background_color = customColors.bg;
    if (customColors.accent) customizations.accent_color = customColors.accent;
    if (!showBranding) customizations.hide_branding = 'true';
    
    if (Object.keys(customizations).length > 0) {
      data.customizations = customizations;
    }

    onGenerate(data);
  };

  const canProceedStep = (s) => {
    switch (s) {
      case 1: return true;
      case 2: return selectedMedia.length === mediaCount;
      case 3: return mode === 'ai' || (textFields.title || textFields.address || textFields.description);
      case 4: return true;
      case 5: return true;
      default: return true;
    }
  };

  // Determine which text fields to show based on template
  const getVisibleTextFields = () => {
    const allFields = [
      { key: 'text', label: 'Headline', max: 30, placeholder: 'New Listing', rows: 0 },
      { key: 'title', label: 'Title', max: 30, placeholder: 'Modern Downtown Condo', rows: 0 },
      { key: 'cta', label: 'Call-to-Action', max: 25, placeholder: 'Jetzt anfragen!', rows: 0 },
      { key: 'details', label: 'Property Details', max: 60, placeholder: '4 Zi · 3 Bad · 245 m²', rows: 0 },
      { key: 'details_1', label: 'Detail 1', max: 10, placeholder: '1200 m²', rows: 0 },
      { key: 'details_2', label: 'Detail 2', max: 10, placeholder: '3 Zi', rows: 0 },
      { key: 'details_3', label: 'Detail 3', max: 10, placeholder: 'Garten', rows: 0 },
      { key: 'address', label: 'Address', max: 80, placeholder: 'Los Angeles,\nCA 90045', rows: 2 },
      { key: 'description', label: 'Description', max: 100, placeholder: 'Property description...', rows: 2 },
      { key: 'subtext', label: 'Subtext', max: 30, placeholder: 'Jetzt Online', rows: 0 },
      { key: 'promo', label: 'Promo Text', max: 30, placeholder: 'Jetzt Anrufen', rows: 0 },
      { key: 'amount', label: 'Price/Amount', max: 30, placeholder: '$770,200', rows: 0 },
      { key: 'price', label: 'Price', max: 30, placeholder: '$ 1,252,000', rows: 0 },
      { key: 'price_text', label: 'Price Intro Text', max: 25, placeholder: 'Jetzt Online für', rows: 0 },
    ];
    
    // Show fields based on template's text_fields array
    if (textFieldsList.length > 0) {
      return allFields.filter(f => textFieldsList.includes(f.key));
    }
    
    // Default: show common fields
    return allFields.filter(f => ['title', 'details', 'address'].includes(f.key));
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-stretch sm:items-center justify-center z-50 sm:p-4">
      <Card data-testid="video-wizard-modal" className="glass border-[#1f4d3a]/50 w-full max-w-4xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[90dvh] rounded-none sm:rounded-xl overflow-hidden">
        <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4 shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center gap-2 text-[#23211a]">
              <Video className="w-5 h-5 text-[#1f4d3a]" />
              Video Wizard
              <span className="text-sm text-[#8a8474] font-normal ml-1">
                {template.name}
              </span>
            </CardTitle>
            <Button variant="ghost" onClick={onCancel} className="text-[#6f6a5c] hover:text-[#23211a]" data-testid="wizard-close-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center gap-1 mt-4">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(num => (
              <div key={num} className="flex items-center flex-1">
                <button
                  onClick={() => num < step && setStep(num)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                    step === num ? 'bg-[#1f4d3a] text-white ring-2 ring-[#1f4d3a]/50' : 
                    step > num ? 'bg-[#1e7a4f] text-white cursor-pointer hover:bg-[#1e7a4f]' : 
                    'bg-[#e9e2d2] text-[#8a8474]'
                  }`}
                >
                  {step > num ? <Check className="w-3.5 h-3.5" /> : num}
                </button>
                {num < TOTAL_STEPS && (
                  <div className={`h-0.5 flex-1 mx-1 rounded ${step > num ? 'bg-[#1e7a4f]' : 'bg-[#e9e2d2]'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1 px-1">
            {STEP_LABELS.map((label, i) => (
              <span key={label} className={`text-[10px] ${step === i + 1 ? 'text-[#1f4d3a] font-medium' : 'text-[#9c9585]'}`}>
                {label}
              </span>
            ))}
          </div>
        </CardHeader>

        <CardContent className="space-y-5 flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6">
          {/* Step 1: Mode Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-[#23211a]">Choose Generation Mode</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  data-testid="mode-ai"
                  onClick={() => setMode('ai')}
                  className={`p-5 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                    mode === 'ai' ? 'border-[#1f4d3a] bg-[#e9efe7]' : 'border-[#e3dccb] hover:border-[#1f4d3a]/40 bg-[#f2ecdf]'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 bg-[#e9efe7] rounded-lg">
                      <Sparkles className="w-5 h-5 text-[#1f4d3a]" />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-[#23211a]">AI Auto-Generate</h4>
                      <p className="text-[#8a8474] text-xs">AI creates all texts automatically</p>
                    </div>
                  </div>
                </div>

                <div
                  data-testid="mode-manual"
                  onClick={() => setMode('manual')}
                  className={`p-5 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                    mode === 'manual' ? 'border-[#c7a869] bg-[#f5eede]' : 'border-[#e3dccb] hover:border-[#c7a869]/60 bg-[#f2ecdf]'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 bg-[#f0e6cf] rounded-lg">
                      <SlidersHorizontal className="w-5 h-5 text-[#8f6f3a]" />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-[#23211a]">Manual Control</h4>
                      <p className="text-[#8a8474] text-xs">Write your own text with AI assist</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#4a463b] mb-1.5 block">Language</label>
                <select
                  data-testid="language-select"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full h-10 rounded-lg bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3 text-sm"
                >
                  <option value="de">Deutsch (German)</option>
                  <option value="en">English</option>
                </select>
              </div>

              {/* Template requirements info */}
              <div data-testid="template-requirements" className="p-3 rounded-lg bg-[#fffdf7] border border-[#e3dccb]">
                <p className="text-xs font-medium text-[#6f6a5c] mb-2">Template Requirements:</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${
                    requiresVideos 
                      ? 'bg-[#1f4d3a]/15 text-[#1f4d3a] border-[#1f4d3a]/20' 
                      : 'bg-sky-600/15 text-sky-300 border-sky-600/20'
                  }`}>
                    {requiresVideos ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                    {mediaCount} {requiresVideos ? 'Videos' : 'Photos'} required
                  </span>
                  {hasVoiceover && (
                    <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#9a6d1f]/15 text-[#9a6d1f] border border-amber-600/20">
                      <Mic className="w-3 h-3" /> Voiceover supported
                    </span>
                  )}
                  {hasBackAudio && (
                    <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#f7e8e3] text-[#a34430] border border-[#b3543f]/40">
                      <Music className="w-3 h-3" /> Background music
                    </span>
                  )}
                </div>
                {requiresVideos && (
                  <p className="text-xs text-[#9a6d1f] mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    This template requires VIDEO files, not images
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Media Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-[#23211a]">
                  Select {requiresVideos ? 'Videos' : 'Images'}
                  <span className="text-xs text-[#8a8474] font-normal ml-2">
                    (exactly {mediaCount} required)
                  </span>
                </h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-[#d8d0bd] text-[#6f6a5c] text-xs h-7" onClick={() => setSelectedMedia([])}>
                    Clear
                  </Button>
                  {!requiresVideos && (
                    <Button size="sm" variant="outline" className="border-[#1f4d3a] text-[#1f4d3a] text-xs h-7" onClick={handleSelectAll}>
                      Select {mediaCount}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Validation message */}
              <div className={`p-2.5 rounded-lg flex items-start gap-2 text-xs ${
                selectedMedia.length === mediaCount 
                  ? 'bg-[#e4f0e8] border border-[#1e7a4f]/40 text-[#1e7a4f]'
                  : 'bg-[#f6edd8] border border-amber-600/30 text-[#9a6d1f]'
              }`}>
                {selectedMedia.length === mediaCount 
                  ? <Check className="w-4 h-4 shrink-0 mt-0.5" />
                  : <Info className="w-4 h-4 shrink-0 mt-0.5" />
                }
                {selectedMedia.length === mediaCount 
                  ? `Perfect! ${mediaCount} ${mediaType}(s) selected`
                  : `Select ${mediaCount - selectedMedia.length} more ${mediaType}(s)`
                }
              </div>

              {/* Show video URL input if template requires videos */}
              {requiresVideos ? (
                <div className="space-y-3">
                  <p className="text-xs text-[#6f6a5c]">
                    Enter URLs for {mediaCount} video files:
                  </p>
                  {Array.from({ length: mediaCount }, (_, i) => (
                    <div key={i}>
                      <label className="text-xs text-[#8a8474] mb-1 block">Video {i + 1}</label>
                      <Input
                        data-testid={`video-url-${i}`}
                        value={selectedMedia[i] || ''}
                        onChange={(e) => {
                          const newMedia = [...selectedMedia];
                          newMedia[i] = e.target.value;
                          setSelectedMedia(newMedia.filter(Boolean));
                        }}
                        placeholder="https://example.com/video.mp4"
                        className="bg-[#f7f3ea]/85 border-[#e3dccb] text-sm"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {property.images?.map((img, index) => (
                    <div
                      key={index}
                      data-testid={`image-option-${index}`}
                      onClick={() => handleMediaToggle(img)}
                      className={`relative aspect-[4/3] rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                        selectedMedia.includes(img)
                          ? 'ring-3 ring-[#1f4d3a] scale-[0.97]'
                          : selectedMedia.length >= mediaCount
                            ? 'ring-1 ring-[#e3dccb] opacity-50 cursor-not-allowed'
                            : 'ring-1 ring-[#e3dccb] hover:ring-[#1f4d3a]/40'
                      }`}
                    >
                      <img src={img} alt={`Property ${index + 1}`} className="w-full h-full object-cover" />
                      {selectedMedia.includes(img) && (
                        <div className="absolute inset-0 bg-[#dfe8db] flex items-center justify-center">
                          <div className="bg-[#1f4d3a] rounded-full p-1.5">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div className="absolute top-2 right-2 bg-[#1f4d3a] rounded-full w-6 h-6 flex items-center justify-center text-white font-bold text-[10px]">
                            {selectedMedia.indexOf(img) + 1}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-[#9c9585]">
                {selectedMedia.length}/{mediaCount} selected
              </p>
            </div>
          )}

          {/* Step 3: Text Fields */}
          {step === 3 && (
            <div className="space-y-4">
              {mode === 'manual' ? (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-[#23211a]">Text Content</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="ai-fill-all-btn"
                      onClick={handleAiAssistAll}
                      disabled={aiAssistLoading === 'all'}
                      className="border-[#1f4d3a] text-[#1f4d3a] text-xs h-7"
                    >
                      {aiAssistLoading === 'all' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                      AI Fill All
                    </Button>
                  </div>

                  {getVisibleTextFields().map(field => (
                    <div key={field.key}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-[#6f6a5c]">{field.label} (max {field.max})</label>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`ai-assist-${field.key}`}
                          onClick={() => handleAiAssistField(field.key)}
                          disabled={aiAssistLoading === field.key}
                          className="text-[#1f4d3a] h-6 text-[10px] px-2 hover:text-[#1f4d3a]"
                        >
                          {aiAssistLoading === field.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 mr-0.5" />}
                          AI
                        </Button>
                      </div>
                      {field.rows > 0 ? (
                        <Textarea
                          data-testid={`text-field-${field.key}`}
                          value={textFields[field.key]}
                          onChange={(e) => setTextFields({...textFields, [field.key]: e.target.value.slice(0, field.max)})}
                          placeholder={field.placeholder}
                          className="bg-[#f7f3ea]/85 border-[#e3dccb] text-sm"
                          rows={field.rows}
                          maxLength={field.max}
                        />
                      ) : (
                        <Input
                          data-testid={`text-field-${field.key}`}
                          value={textFields[field.key]}
                          onChange={(e) => setTextFields({...textFields, [field.key]: e.target.value.slice(0, field.max)})}
                          placeholder={field.placeholder}
                          className="bg-[#f7f3ea]/85 border-[#e3dccb] text-sm"
                          maxLength={field.max}
                        />
                      )}
                      <p className="text-[10px] text-[#9c9585] mt-0.5">{textFields[field.key]?.length || 0}/{field.max}</p>
                    </div>
                  ))}

                  {hasVoiceover && enableVoiceover && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-[#6f6a5c]">Voiceover Script (max 200)</label>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="ai-assist-voiceover"
                          onClick={() => handleAiAssistField('voiceover')}
                          disabled={aiAssistLoading === 'voiceover'}
                          className="text-[#1f4d3a] h-6 text-[10px] px-2 hover:text-[#1f4d3a]"
                        >
                          {aiAssistLoading === 'voiceover' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 mr-0.5" />}
                          AI
                        </Button>
                      </div>
                      <Textarea
                        data-testid="text-field-voiceover"
                        value={textFields.voiceover}
                        onChange={(e) => setTextFields({...textFields, voiceover: e.target.value.slice(0, 200)})}
                        placeholder="Wunderschöne Immobilie mit atemberaubender Aussicht..."
                        className="bg-[#f7f3ea]/85 border-[#e3dccb] text-sm"
                        rows={3}
                        maxLength={200}
                      />
                      <p className="text-[10px] text-[#9c9585] mt-0.5">{textFields.voiceover?.length || 0}/200</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#e9efe7] border border-[#1f4d3a]/20 rounded-lg p-6 text-center">
                  <Sparkles className="w-10 h-10 text-[#1f4d3a] mx-auto mb-3" />
                  <p className="text-[#4a463b] text-sm mb-3">AI will automatically generate optimized text in {language === 'de' ? 'German' : 'English'}</p>
                  <div className="grid grid-cols-2 gap-2 max-w-sm mx-auto text-left">
                    {['Title & Details', 'Address info', 'Call-to-action', ...(hasVoiceover ? ['Voiceover script'] : [])].map(item => (
                      <div key={item} className="flex items-center gap-1.5 text-xs text-[#6f6a5c]">
                        <Zap className="w-3 h-3 text-[#1f4d3a] shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Style & Branding */}
          {step === 4 && (
            <div className="space-y-5">
              <h3 className="text-base font-semibold text-[#23211a] flex items-center gap-2">
                <Palette className="w-4 h-4 text-[#1f4d3a]" />
                Style & Branding
              </h3>

              {/* Color presets */}
              <div>
                <label className="text-xs font-medium text-[#6f6a5c] mb-2 block">Color Theme</label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {COLOR_PRESETS.map((preset, i) => (
                    <button
                      key={preset.name}
                      data-testid={`color-preset-${i}`}
                      onClick={() => setColorPreset(i)}
                      className={`p-2.5 rounded-lg text-center transition-all duration-200 border-2 ${
                        colorPreset === i ? 'border-[#1f4d3a] bg-[#e9efe7]' : 'border-[#e3dccb] hover:border-[#1f4d3a]/40 bg-[#f2ecdf]'
                      }`}
                    >
                      <div className="flex justify-center gap-1 mb-1.5">
                        {i === 0 ? (
                          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#c9c1ac] to-[#b5ad99] border border-[#c9c1ac]" />
                        ) : (
                          <>
                            <div className="w-4 h-4 rounded-full border border-[#d8d0bd]" style={{ backgroundColor: preset.title }} />
                            <div className="w-4 h-4 rounded-full border border-[#d8d0bd]" style={{ backgroundColor: preset.accent }} />
                          </>
                        )}
                      </div>
                      <span className="text-[10px] text-[#6f6a5c]">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom colors */}
              {colorPreset > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'title', label: 'Title Color' },
                    { key: 'bg', label: 'Background' },
                    { key: 'accent', label: 'Accent' },
                  ].map(c => (
                    <div key={c.key}>
                      <label className="text-[10px] text-[#8a8474] mb-1 block">{c.label}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customColors[c.key] || '#000000'}
                          onChange={(e) => setCustomColors({ ...customColors, [c.key]: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                        />
                        <Input
                          value={customColors[c.key]}
                          onChange={(e) => setCustomColors({ ...customColors, [c.key]: e.target.value })}
                          className="bg-[#f7f3ea]/85 border-[#e3dccb] text-xs h-8 font-mono"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Branding toggle */}
              <div className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
                <div>
                  <p className="text-sm text-[#23211a]">Include Brand Info</p>
                  <p className="text-[10px] text-[#8a8474]">Show your name, email & phone in the outro</p>
                </div>
                <Switch checked={showBranding} onCheckedChange={setShowBranding} data-testid="branding-toggle" />
              </div>
            </div>
          )}

          {/* Step 5: Audio */}
          {step === 5 && (
            <div className="space-y-5">
              {/* Voiceover toggle (only if template supports it) */}
              {hasVoiceover && (
                <div className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg border border-[#e3dccb]">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-[#8f6f3a]" />
                    <div>
                      <p className="text-sm text-[#23211a]">AI Voiceover</p>
                      <p className="text-[10px] text-[#8a8474]">
                        {enableVoiceover ? 'AI will speak the voiceover text' : 'Voiceover disabled'}
                      </p>
                    </div>
                  </div>
                  <Switch checked={enableVoiceover} onCheckedChange={setEnableVoiceover} data-testid="voiceover-toggle" />
                </div>
              )}

              {/* Background music (only if template supports it) */}
              {hasBackAudio && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Music className="w-4 h-4 text-[#1f4d3a]" />
                    <h3 className="text-sm font-semibold text-[#23211a]">Background Music</h3>
                    <span className="text-[10px] text-[#8a8474]">(optional)</span>
                  </div>

                  {/* ── Preset Music ── */}
                  {presetMusic.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-[#6f6a5c]">
                          {language === 'de'
                            ? 'Track antippen = anhören und auswählen'
                            : 'Tap a track to preview and select it'}
                        </p>
                        {selectedMusic && (
                          <button
                            type="button"
                            onClick={clearMusic}
                            className="text-[10px] text-[#8a8474] hover:text-[#4a463b] underline underline-offset-2"
                          >
                            {language === 'de' ? 'Keine Musik' : 'No music'}
                          </button>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        {presetMusic.map(preset => {
                          const isSelected = selectedMusic === preset.url;
                          const isPlaying = playingPreview === preset.id;
                          return (
                          <div
                            key={preset.id}
                            data-testid={`preset-${preset.id}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => selectTrack(preset.id, preset.url)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectTrack(preset.id, preset.url); } }}
                            className={`p-2.5 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2 ${
                              isSelected
                                ? 'bg-[#e9efe7] border border-[#1f4d3a]'
                                : 'bg-[#f2ecdf] border border-[#e3dccb] hover:border-[#1f4d3a]/40'
                            }`}
                          >
                            {/* Play/Pause button — toggles audio without de-selecting */}
                            <button
                              type="button"
                              aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                              onClick={(e) => { e.stopPropagation(); togglePreview(preset.id, preset.url); }}
                              className="flex-shrink-0 w-7 h-7 rounded-full bg-[#dfe8db] hover:bg-[#1f4d3a]/50 flex items-center justify-center transition-colors"
                            >
                              {isPlaying
                                ? <Pause className="w-3 h-3 text-[#1f4d3a]" />
                                : <Play className="w-3 h-3 text-[#1f4d3a] ml-0.5" />
                              }
                            </button>
                            {/* Track info */}
                            <div className="flex-1 min-w-0">
                              <span className="text-xs text-[#23211a] font-medium block truncate">{preset.name}</span>
                              <span className="text-[10px] text-[#8a8474] block truncate">
                                {isPlaying ? (language === 'de' ? '♪ wird abgespielt …' : '♪ playing …') : preset.description}
                              </span>
                            </div>
                            {isSelected && (
                              <span className="flex items-center gap-1 flex-shrink-0">
                                <Check className="w-3.5 h-3.5 text-[#1f4d3a]" />
                                <span className="text-[10px] text-[#1f4d3a] font-medium hidden sm:inline">
                                  {language === 'de' ? 'Ausgewählt' : 'Selected'}
                                </span>
                              </span>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Custom Upload ── */}
                  <p className="text-xs text-[#6f6a5c] mb-2">Or upload your own</p>
                  <label htmlFor="music-upload" className="cursor-pointer block mb-3">
                    <div className="border-2 border-dashed border-[#d8d0bd] hover:border-[#1f4d3a] rounded-lg p-3 text-center transition-all duration-200">
                      <Upload className="w-5 h-5 text-[#8a8474] mx-auto mb-1" />
                      <p className="text-xs text-[#6f6a5c]">{uploadingMusic ? 'Uploading...' : 'Upload Music (MP3, WAV, OGG)'}</p>
                    </div>
                  </label>
                  <input id="music-upload" type="file" accept="audio/*" onChange={handleMusicUpload} className="hidden" disabled={uploadingMusic} />

                  {musicLibrary.length > 0 && (
                    <div className="space-y-1.5 max-h-36 overflow-y-auto">
                      {musicLibrary.map(music => {
                        const isSelected = selectedMusic === music.file_url;
                        const isPlaying = playingPreview === music.id;
                        return (
                        <div
                          key={music.id}
                          data-testid={`music-item-${music.id}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => selectTrack(music.id, music.file_url)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectTrack(music.id, music.file_url); } }}
                          className={`p-2.5 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2 ${
                            isSelected
                              ? 'bg-[#e9efe7] border border-[#1f4d3a]'
                              : 'bg-[#f2ecdf] border border-[#e3dccb] hover:border-[#1f4d3a]/40'
                          }`}
                        >
                          <button
                            type="button"
                            aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                            onClick={(e) => { e.stopPropagation(); togglePreview(music.id, music.file_url); }}
                            className="flex-shrink-0 w-7 h-7 rounded-full bg-[#dfe8db] hover:bg-[#1f4d3a]/50 flex items-center justify-center transition-colors"
                          >
                            {isPlaying
                              ? <Pause className="w-3 h-3 text-[#1f4d3a]" />
                              : <Play className="w-3 h-3 text-[#1f4d3a] ml-0.5" />
                            }
                          </button>
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <Music className="w-3.5 h-3.5 text-[#1f4d3a] flex-shrink-0" />
                            <span className="text-xs text-[#23211a] truncate">
                              {isPlaying ? (language === 'de' ? '♪ wird abgespielt …' : '♪ playing …') : music.name}
                            </span>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 text-[#1f4d3a] flex-shrink-0" />}
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {selectedMusic && (
                    <div className="mt-3">
                      <label className="text-xs text-[#6f6a5c] mb-1 block">Volume: {musicVolume}</label>
                      <input
                        data-testid="music-volume-slider"
                        type="range"
                        min="0"
                        max="100"
                        value={parseInt(musicVolume)}
                        onChange={(e) => setMusicVolume(`${e.target.value}%`)}
                        className="w-full accent-[#1f4d3a]"
                      />
                      <p className="text-[10px] text-[#9c9585]">
                        {enableVoiceover ? 'Recommended: 20-40% with voiceover' : 'Recommended: 50-100% without voiceover'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!hasBackAudio && !hasVoiceover && (
                <div className="p-4 rounded-lg bg-[#fffdf7] border border-[#e3dccb] text-center">
                  <Info className="w-8 h-8 text-[#8a8474] mx-auto mb-2" />
                  <p className="text-sm text-[#6f6a5c]">This template does not support custom audio</p>
                </div>
              )}
            </div>
          )}
        </CardContent>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3 shrink-0 px-4 sm:px-6 pt-3 sm:pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-[#e3dccb]">
          <Button
            variant="outline"
            data-testid="wizard-back-btn"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="border-[#d8d0bd] text-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {step < TOTAL_STEPS ? (
            <Button
              data-testid="wizard-next-btn"
              onClick={() => setStep(step + 1)}
              disabled={!canProceedStep(step)}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-sm"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="wizard-generate-btn"
              onClick={handleGenerate}
              disabled={generatingVideo || !canProceedStep(step)}
              className="bg-[#8f6f3a] hover:bg-[#75592e] glow-secondary text-sm"
            >
              {generatingVideo ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Generate Video
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

// Video icon component
const Video = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11"/>
    <rect x="2" y="6" width="14" height="12" rx="2"/>
  </svg>
);
